import { CartState, RestaurantState, AuthState, SuitesState } from '@/store/types';
import { DateTime } from 'luxon';
import { parsePhoneNumberFromString, CountryCode, PhoneNumber } from 'libphonenumber-js/min';
import i18n from '@/i18n';
import store from '@/store';

/**
 * Creates luxon DateTime object from order information to use for formatting
 *
 * @param {CheckoutPickupInfo} pickupInformation
 * @return {DateTime}
 */
function getDateTimeFromOrder(pickupInformation: CheckoutPickupInfo): DateTime {
	let returnDate = DateTime.fromISO(pickupInformation.dueByDate!).setLocale(i18n.locale);
	if(pickupInformation!.dueByTime) {
		const timeAsDate = DateTime.fromFormat('1/1/2020 ' + pickupInformation!.dueByTime, i18n.t('checkout.review.user_details.local_date_format'));
		returnDate = returnDate.set({ hour: timeAsDate.hour, minute: timeAsDate.minute });
	}
	if (pickupInformation && !pickupInformation.scheduled && pickupInformation.prepTime){
		let hour, minute;
		if (pickupInformation.prepTime > 60 ) {
			minute = pickupInformation.prepTime % 60;
			hour = Math.floor(pickupInformation.prepTime / 60);
		}
		else {
			minute = pickupInformation.prepTime;
			hour = 0;
		}
		returnDate = returnDate.plus({ hours: hour, minutes: minute });
	}

	return returnDate;
}

/**
 * Format date for user display
 *
 * @param {CheckoutPickupInfo} pickupInformation
 * @return {string}
 */
export function formatDate(pickupInformation: CheckoutPickupInfo): string {
	return getDateTimeFromOrder(pickupInformation).toFormat(i18n.t('checkout.confirmation.date_format'));
}

/**
 * Format time for user display
 *
 * @param {CheckoutPickupInfo} pickupInformation
 * @return {string}
 */
export function formatTime(pickupInformation: CheckoutPickupInfo): string {
	return getDateTimeFromOrder(pickupInformation).toFormat(i18n.t('checkout.confirmation.time_format'));
}

/**
 * Format date and time for user display
 *
 * @param {CheckoutPickupInfo} pickupInformation
 * @return {string}
 */
export function formatTakeoutDateTime(pickupInformation: CheckoutPickupInfo): string {
	return getDateTimeFromOrder(pickupInformation).toFormat(pickupInformation.dueByTime ? i18n.t('checkout.confirmation.date_format_with_time') : i18n.t('checkout.confirmation.date_format'));
}

/**
 * Format date and time for user display
 *
 * @param {string} date
 * @return {string}
 */
export function formatDateTime(date: string): string {
	return DateTime.fromISO(date).setLocale(i18n.locale).toFormat(i18n.t('checkout.confirmation.date_format_with_time'));
}

/**
 * Set the temp option
 *
 * @param {OptionGroup} optionGroup - the group option
 * @param {Option} option - the value of the option
 * @return {void}
 */
export function setTemporaryOption(optionGroup: OptionGroup, option: Option): OrderOptionGroup {
	return {
		id: optionGroup.id,
		name: optionGroup.name,
		localization: optionGroup.localization,
		optionGroupSku: optionGroup.sku ? optionGroup.sku : null,
		optionGroupType: optionGroup.type,
		givexType: optionGroup.givex_type,
		requiredOption: optionGroup.required,
		multipleOption: optionGroup.allow_multiple_selection,
		order: optionGroup.order,
		memo: false,
		values: [
			formatTemporarySelectedOrderOption(option) as OrderOption
		]
	};
}

/**
 * Format the order item to remove unnecessary fields
 *
 * @param {MenuItem} item
 * @return {OrderItem}
 */
export function formatTemporarySelectedOrderItem(item: MenuItem): Partial<MenuItem> {
	return {
		id: item.id,
		menu_group_id: item.menu_group_id,
		menu_id: item.menu_id,
		section_id: item.section_id,
		parent: item.parent,
		name: item.name,
		localization: item.localization,
		slug: item.slug,
		sku: item.sku ? item.sku : null,
		category: item.category ? item.category : null,
		price: item.price,
		price_type: item.price_type,
		adjustedPrice: item.adjustedPrice,
		promos: item.promos,
		alcoholic_beverage: item.alcoholic_beverage,
		featured_item: item.featured_item,
		upsell_item: item.upsell_item,
		meal_package: item.meal_package,
		price_on_request: item.price_on_request,
		tax_rate: item.tax_rate ? item.tax_rate : null,
		tax_rebate_eligibility: item.tax_rebate_eligibility ? item.tax_rebate_eligibility : 'eligible',
		quantity: 1,
		options: item.options,
		orderOptions: []
	};
}

/**
 * Format the order item options to remove unnecessary fields
 *
 * @param {MenuItem} item
 * @return {OrderItem}
 */
export function formatTemporarySelectedOrderOption(option: Option): Partial<Option> {
	return {
		id: option.id,
		name: option.name,
		localization: option.localization,
		calories: option.calories,
		product_id: option.product_id ? option.product_id : null,
		sku: option.sku ? option.sku : null,
		category: option.category ? option.category : null,
		tax_rate: option.tax_rate ? option.tax_rate : null,
		price: option.price,
		adjustedPrice: option.adjustedPrice,
		promos: option.promos,
		roll_up: option.roll_up,
		order: option.order,
		quantity: typeof option.quantity === 'number' ? option.quantity : 1,
		default_selection: option.default_selection
	};
}

/**
 * Format the order options for comparision to increase quantity or not. We are formatting time
 * to compare them with JSON.stringify. The reason we are doing this is because we have multiple
 * identifiers that may or may not change in the future, this is the easiest way to future proof
 * any changes.
 *
 * @param {OrderOptionGroup[] | null | undefined} orderOptions
 * @return {OrderOptionGroup}
 */
export function formatOrderOptionsToCompare(orderOptions: OrderOptionGroup[] | null | undefined): any {
	if(orderOptions && orderOptions.length) {

		// Order option groups
		const tempOrderOptionGroups = orderOptions!.map((orderOptionGroup: OrderOptionGroup) => {

			// Options
			const tempOptions = orderOptionGroup.values!.map((value: OrderOption) => {
				let tempSubOptionGroups: OrderOptionGroup[] | undefined = value.options;

				// Sub options
				tempSubOptionGroups = formatOrderOptionsToCompare(tempSubOptionGroups);

				// Return options
				return {
					id: value.id,
					sku: value.sku,
					name: value.name,
					localization: value.localization,
					options: tempSubOptionGroups,
					quantity: value.quantity
				};
			});

			// Return order option group
			return {
				id: orderOptionGroup.id,
				optionGroupSku: orderOptionGroup.optionGroupSku,
				name: orderOptionGroup.name,
				localization: orderOptionGroup.localization,
				memo: orderOptionGroup.memo,
				values: tempOptions
			};
		});
		return tempOrderOptionGroups;
	}
	return undefined;
}

/**
 * Format the order before submitting to the backend
 *
 * @param {CartState} import
 * @return {void}
 */
export function formatOrder(state: CartState, restaurantState: RestaurantState, authState: AuthState, suitesState: SuitesState): any {
	return {
		config: {
			restaurantId: restaurantState.restaurant.app8_restaurant,
			tableNum: state.config.tableNum,
			userId: authState.user.id ? authState.user.id : null,
			tableInfo: state.config.tableInfo,
			memberId: state.checkout.memberInfo && state.checkout.memberInfo.id ? state.checkout.memberInfo.id : null,
			token: authState.user.token ? authState.user.token : null,
			...(restaurantState.restaurant.another_round && { orderCartUrl: window.location.href })
		},
		items: state.items,
		costs: state.costs,
		checkout: {
			pickup: {
				delivery: state.checkout.pickup && state.checkout.pickup.delivery ? state.checkout.pickup.delivery : false,
				notes: state.checkout.pickup && state.checkout.pickup.notes ? state.checkout.pickup.notes : null
			},
			delivery: (state.checkout.pickup && state.checkout.pickup.delivery) && (state.checkout.delivery && state.checkout.delivery.type) ? state.checkout.delivery : null,
			no_takeout_timeslot: restaurantState.restaurant.noTakeoutTimeslot,
			questions: state.checkout.questions?.length ? state.checkout.questions : null,
			contact: {
				full_name: state.checkout.contact.full_name,
				email: state.checkout.contact.email,
				phone_number: state.checkout.contact.phone_number
			},
			loyaltyProgram: state.checkout.loyaltyProgram,
			card: state.checkout.card?.number ? state.checkout.card : null,
			invoice: state.checkout.invoice,
			costCenter: state.checkout.costCenter,
			purchaseOrder: state.checkout.purchaseOrder,
			applePayPaymentToken: state.checkout.applePayPaymentToken,
			applePayPaymentDetails: state.checkout.applePayPaymentDetails,
			googlePayPaymentPayload: state.checkout.googlePayPaymentPayload,
			memberInfo: state.checkout.memberInfo && state.checkout.memberInfo.id
				? { id: state.checkout.memberInfo.id, identifier: state.checkout.memberInfo.identifier, provider: state.checkout.memberInfo.provider, promosApplied: state.checkout.memberInfo.promosApplied ? Array.from(state.checkout.memberInfo.promosApplied) : null, promos: state.checkout.memberInfo.promos }
				: null,
			paymentNotRequired: state.checkout.paymentNotRequired
		},
		chosenCreditCard: {
			paymentCardId: state.chosenCreditCard ? state.chosenCreditCard.paymentCardId : null
		},
		suites: (suitesState.preOrdering || suitesState.eventDayOrdering) && suitesState.minimalEventInfo ? {
			suitesLocationId: suitesState.suitesLocationId,
			eventDayOrder: suitesState.eventDayOrdering,
			eventDayPaymentPreference: suitesState.minimalEventInfo.eventSuite.event_day_payment_preference,
			eventSuiteId: suitesState.minimalEventInfo.eventSuite.id,
			eventSuiteName: suitesState.minimalEventInfo.eventSuite.suite.name,
			// this will become an array of emails once we have additional_contacts implemented
			eventContactInfo: [authState.user.email],
			eventStartDate: suitesState.minimalEventInfo.event.start_date,
			eventLockPeriod: suitesState.minimalEventInfo.event.order_lock_period,
			eventId: suitesState.minimalEventInfo?.event.id,
			eventName: suitesState.minimalEventInfo.event.title,
			suiteOperatorEmail: authState.suiteOperator?.email ? authState.suiteOperator.email : undefined,
			eventManagerInfo: suitesState.userEventSuite && suitesState.userEventSuite.event && suitesState.userEventSuite.event.manager_info ? suitesState.userEventSuite.event.manager_info : null,
			orderId: suitesState.suiteCateringOrder ? suitesState.suiteCateringOrder.id : null,
			orderDeliveryTime: state.checkout.suitesInfo && state.checkout.suitesInfo.deliveryTime ? state.checkout.suitesInfo.deliveryTime : null,
			orderNotes: state.checkout.suitesInfo && state.checkout.suitesInfo.notes ? state.checkout.suitesInfo.notes : null,
			suiteOwner: {
				id: suitesState.minimalEventInfo.eventSuite.suite_owner.id,
				email: authState.user ? authState.user.email : null,
				companyName: suitesState.minimalEventInfo.eventSuite.suite_owner.company_name
			},
			tabLimit: state.checkout.suitesInfo?.tabLimit ? state.checkout.suitesInfo.tabLimit : null
		} : undefined,
		rotationalMenuOrders: restaurantState.restaurant.k12 && state.rotationalMenuOrders.length ? state.rotationalMenuOrders : null,
		saveVoucher: !!(state.costs.voucher?.code && state.saveVoucher),
		date: DateTime.local().toISO()
	};
}

/**
 * Format the order item from the event suite order
 *
 * @param {OrderItem} tempOrderItem
 * @param {MenuItem} menuItem
 * @return {OrderItem}
 */
export function formatOrderItemFromEventSuiteOrder(tempOrderItem: OrderItem, menuItem: MenuItem): OrderItem {
	return {
		id: menuItem.id,
		menu_group_id: menuItem.menu_group_id,
		menu_id: menuItem.menu_id,
		section_id: menuItem.section_id,
		alcoholic_beverage: menuItem.alcoholic_beverage,
		featured_item: menuItem.featured_item,
		upsell_item: menuItem.upsell_item,
		meal_package: menuItem.meal_package,
		price_on_request: menuItem.price_on_request,
		category: menuItem.category,
		name: menuItem.name,
		localization: menuItem.localization,
		orderId: tempOrderItem.orderId,
		orderPrice: tempOrderItem.orderPrice,
		price: menuItem.adjustedPrice ? menuItem.adjustedPrice : menuItem.price,
		promos: menuItem.promos,
		quantity: tempOrderItem.quantity,
		sku: menuItem.sku,
		slug: menuItem.slug,
		tax_rate: menuItem.tax_rate,
		tax_rebate_eligibility: menuItem.tax_rebate_eligibility
	} as unknown as OrderItem;
}

/**
 * Format the order options of the item from the event suite order
 *
 * @param {OrderItem} tempOrderItem
 * @param {MenuItem} menuItem
 * @param {OrderOption} tempOrderOption
 * @param {OptionGroup | undefined} optionGroup
 * @return {OrderOption}
 */
export function formatOrderOptionFromEventSuiteOrder(tempOrderItem: OrderItem, menuItem: MenuItem, tempOrderOption: OrderOption, optionGroup: OptionGroup | undefined): OrderOption {
	let orderOption: OrderOptionGroup = {} as OrderOptionGroup;

	// Option group
	if(optionGroup) {
		orderOption = {
			id: optionGroup.id,
			name: optionGroup.name,
			localization: optionGroup.localization,
			optionGroupSku: optionGroup.sku as string,
			optionGroupType: optionGroup.type,
			givexType: optionGroup.givex_type,
			requiredOption: optionGroup.required,
			multipleOption: optionGroup.allow_multiple_selection,
			order: optionGroup.order,
			memo: tempOrderOption.memo
		};

		// Options
		if (tempOrderOption.values) {
			orderOption.values = tempOrderOption.values.map((tempOrderOptionValue: Option) => {
				let orderOptionValue = {} as OrderOption;
				const option = optionGroup.values && optionGroup.values.find((option: Option) => option.id === tempOrderOptionValue.id);
				if(option) {
					orderOptionValue = {
						id: option.id,
						name: option.name,
						localization: option.localization,
						calories: option.calories,
						product_id: option.product_id,
						sku: option.sku,
						category: option.category,
						tax_rate: option.tax_rate,
						price: option.adjustedPrice ? option.adjustedPrice : option.price,
						promos: option.promos,
						roll_up: option.roll_up,
						order: option.order,
						quantity: typeof tempOrderOptionValue.quantity === 'number' ? tempOrderOptionValue.quantity : 1,
						default_selection: option.default_selection
					};

					// Recursivity, if there are any sub options
					if(tempOrderOptionValue.options) {
						orderOptionValue.options = tempOrderOptionValue.options.map((subTempOrderOption: OrderOption) => {
							const subOptionGroup = option.options && option.options.find((tempSubOptionGroup: OptionGroup) => tempSubOptionGroup.id === subTempOrderOption.id);
							if(subOptionGroup) {
								return formatOrderOptionFromEventSuiteOrder(tempOrderItem, menuItem, subTempOrderOption, subOptionGroup);
							}
							else {
								throw new Error('Sub option group not found');
							}
						}) as OrderOptionGroup[];
					}

					return orderOptionValue;
				}
				else {
					throw new Error('Option not found');
				}
			});
		}
	}
	// Special instructions
	else {
		orderOption = {
			memo: true,
			name: tempOrderOption.name,
			localization: tempOrderOption.localization,
			values: [
				{
					name: tempOrderOption.values[0].name,
					localization: tempOrderOption.values[0].localization,
					price: tempOrderOption.values[0].price,
					roll_up: tempOrderOption.values[0].roll_up,
					quantity: tempOrderOption.values[0].quantity
				}
			]
		} as any;
	}

	return orderOption;
}

/**
 * Remove any non-digital characters from inputs
 *
 * @param {string} value - input value
 * @return {void}
 */
function removeNonDigitalCharacters(value: string): string {
	return value.replace(/\D/g,'');
}

/**
 * Format CVD to respect the format with no digital characters
 *
 * @param {string} cvd - cvd code
 * @return {void}
 */
export function formatCVD(cvd: string): string {
	return removeNonDigitalCharacters(cvd);
}

/**
 * Format the credit card numbers into 4 digits separated by dashes
 *
 * @param {string} cardNumber - cvd code
 * @return {string}
 */
export function formatCCNumber(cardNumber: string): string {
	let output = removeNonDigitalCharacters(cardNumber);
	if (output) {
		output = output.match(/.{1,4}/g)!.join('-');
	}
	return output;
}

/**
 * Format the expiry date with a slash between month and year
 * @param {string} date - expiry date
 * @return {string}
 */
export function formatExpiryDate(date: string): string {
	let output = removeNonDigitalCharacters(date);
	if (output) {
		output = output.match(/.{1,2}/g)!.join('/');
	}
	return output;
}

/**
 * Format the credit card in order to save it
 * @param {CheckoutCardInfo} checkoutCard
 * @return {PaymentOption}
 */
export function formatCheckoutCardBeforeSave(checkoutCard: CheckoutCardInfo): PaymentOption {
	return {
		nameOnCard: checkoutCard.nameOnCard,
		ccNumber: checkoutCard.number,
		expiryDate: checkoutCard.expiry_date,
		cvs: checkoutCard.cvd,
		postalCode: checkoutCard.postal_code,
		isDefault: true
	};
}

/**
 * Normalize a stored phone value: recognizable shapes ('+'-prefixed, or bare
 * legacy NANP digits which are assumed +1) come back as compact E.164.
 * Anything else comes back as bare digits.
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: '+12223334444', or '223334444' when unrecognizable
 */
function normalizePhoneNumber(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	if (phoneNumber.startsWith('+')) {
		return '+' + phoneNumber.replace(/\D/g, '');
	}
	const digits = phoneNumber.replace(/\D/g, '');
	if (digits.length === 10) {
		return `+1${digits}`;
	}
	if (digits.length === 11 && digits.startsWith('1')) {
		return `+${digits}`;
	}
	return digits;
}

/**
 * Format a phone number for read-only display in international format.
 * Unparseable values are returned verbatim so legacy records are never destroyed in display.
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: +1 222 333 4444
 */
export function formatPhoneNumberForDisplay(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	const e164 = normalizePhoneNumber(phoneNumber);
	if (e164.startsWith('+')) {
		const parsed = parsePhoneNumberFromString(e164);
		if (parsed?.isValid()) {
			return parsed.formatInternational();
		}
	}
	return phoneNumber;
}

/**
 * Parse a phone value: values with a country code and bare legacy shapes
 * are self-describing; any other national shape (e.g. UK '07123 456789') is
 * interpreted with the restaurant's country.
 *
 * @param {string} phoneNumber
 * @return {PhoneNumber | undefined}
 */
function parsePhoneNumber(phoneNumber: string): PhoneNumber | undefined {
	const normalized = normalizePhoneNumber(phoneNumber);
	if (normalized.startsWith('+')) {
		return parsePhoneNumberFromString(normalized);
	}
	const country = (store.getters['restaurant/getCountry'] as CountryCode) || 'CA';
	return parsePhoneNumberFromString(phoneNumber, country);
}

/**
 * Format a phone number as a national number (ie. without the +XX country code)
 * Incomplete or unparseable values are returned as-is so in-progress input is never mangled.
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: '(222) 333-4444' for North America, '07123 456789' for UK
 */
export function formatPhoneNumberToNational(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	const parsed = parsePhoneNumber(phoneNumber);
	if (parsed?.isValid()) {
		return parsed.formatNational();
	}
	return phoneNumber;
}

/**
 * Convert a phone number to the international format.
 * Values the library cannot validate are returned unchanged — no '+' prefix
 * which would flip them into the strict international validation branch
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: +1 222 333 4444
 */
export function formatPhoneNumberToInternational(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	const parsed = parsePhoneNumber(phoneNumber);
	if (parsed?.isValid()) {
		return parsed.formatInternational();
	}
	return phoneNumber;
}

/**
 * Format repeating orders unavailable dates for display (when 3 or fewer)
 *
 * @param {string[]} dates
 * @return {string}
 */
export function formatRepeatingOrdersUnavailableDates(dates: string[]): string {
	const dateFormat = i18n.t('menu.repeating_orders.date_format');
	return dates
		.slice(0, 3)
		.map(date => DateTime.fromISO(date).setLocale(i18n.locale).toFormat(dateFormat as string))
		.join(', ');
}

/**
 * Generate HTML for repeating orders unavailable dates list (when more than 3)
 *
 * @param {string[]} dates
 * @param {string} mealType
 * @return {string}
 */
export function formatRepeatingOrdersUnavailableDatesListHtml(dates: string[], mealType: string): string {
	const dateFormat = i18n.t('menu.repeating_orders.date_format');
	const header = i18n.t('menu.repeating_orders.unavailable_dates_header', { mealType });
	const listItems = dates
		.map(date => `<li>${DateTime.fromISO(date).setLocale(i18n.locale).toFormat(dateFormat)}</li>`)
		.join('');
	return `${header}<ul style="margin-top: 0.5em; padding-left: 0.5em; list-style: disc;">${listItems}</ul>`;
}

/**
 * Prefix an order id with the restaurant's internal identifier for display
 * (e.g. id 73 + identifier "A" -> "A73").
 *
 * @param {string} orderId
 * @param {Restaurant} restaurant
 * @return {string}
 */
export function formatOrderNumber(orderId: string, restaurant: Restaurant): string {
	if(!restaurant || !restaurant.internal_identifier) {
		return orderId;
	}
	return `${restaurant.internal_identifier}${orderId}`;
}
